import { useTranslation } from 'react-i18next'
import { ToolCard } from './tool-card'
import { ToolCardSkeleton } from './tool-card-skeleton'
import { NotionToolCard } from './notion-tool-card'
import { isNotionTool } from './notion-tool-utils'
import type { Tool, ToolsByCategoryResponse } from '@/services/tools'
import { useOrganizationStore } from '@/stores/organization-store'
import { Wrench } from 'lucide-react'

interface ToolGridProps {
  tools?: Tool[]
  toolsByCategory?: ToolsByCategoryResponse[]
  isLoading?: boolean
  groupByCategory?: boolean
  onToolClick?: (tool: Tool) => void
  onToolConfigure?: (tool: Tool) => void
}

export function ToolGrid({
  tools,
  toolsByCategory,
  isLoading = false,
  groupByCategory = false,
  onToolClick,
  onToolConfigure,
}: ToolGridProps) {
  const { t } = useTranslation('tools')
  const { currentOrganization } = useOrganizationStore()
  const organizationId = currentOrganization?.id || ''

  // Render the appropriate card based on tool type
  const renderToolCard = (tool: Tool) => {
    if (isNotionTool(tool)) {
      return (
        <NotionToolCard
          key={tool.id}
          tool={tool}
          organizationId={organizationId}
          onClick={() => onToolClick?.(tool)}
          onConfigure={() => onToolConfigure?.(tool)}
        />
      )
    }

    return (
      <ToolCard
        key={tool.id}
        tool={tool}
        onClick={() => onToolClick?.(tool)}
        onConfigure={() => onToolConfigure?.(tool)}
      />
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <ToolCardSkeleton key={index} />
        ))}
      </div>
    )
  }

  // Grouped by category view
  if (groupByCategory && toolsByCategory) {
    const categoriesWithTools = toolsByCategory.filter((cat) => cat.count > 0)

    if (categoriesWithTools.length === 0) {
      return <EmptyState t={t} />
    }

    return (
      <div className="space-y-8">
        {categoriesWithTools.map((category) => (
          <div key={category.category}>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>{category.category_label}</span>
              <span className="text-sm font-normal text-gray-500">
                ({category.count})
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.tools.map((tool) => renderToolCard(tool))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Flat list view
  if (!tools || tools.length === 0) {
    return <EmptyState t={t} />
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {tools.map((tool) => renderToolCard(tool))}
    </div>
  )
}

// Empty state component
function EmptyState({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <Wrench className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('list.emptyState')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {t('list.emptyStateDesc')}
      </p>
    </div>
  )
}
